import { type ReactNode, useEffect, useRef } from 'react';
import { useGetCoveragePayerHistoryQuery } from '@features/patient-coverages/store/api';
import { useEventTracking } from '@services/statsig';

import { PayerHistoryPanel } from './PayerHistoryPanel';

export function CoveragePayerHistory({
  patientId,
  coverageId,
  coverageLabel,
  children,
}: {
  patientId: string;
  coverageId: string;
  coverageLabel?: string;
  children?: (history: ReactNode) => ReactNode;
}) {
  const { eligibilityPayerHistory: tracking } = useEventTracking();
  const query = useGetCoveragePayerHistoryQuery(
    { patientId, coverageId, limit: 25 },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );
  const activeRequest = useRef<{ requestId: string; startedAt: number } | undefined>(undefined);
  const {
    requestId,
    startedTimeStamp,
    fulfilledTimeStamp,
    originalArgs,
    isFetching,
    isSuccess,
    isError,
  } = query;
  useEffect(() => {
    // RTK Query may retain the previous policy's result during an argument
    // change. Only observe requests belonging to this mounted policy.
    if (originalArgs?.patientId !== patientId || originalArgs.coverageId !== coverageId) {
      activeRequest.current = undefined;
      return;
    }
    if (isFetching && requestId && startedTimeStamp !== undefined) {
      activeRequest.current = { requestId, startedAt: startedTimeStamp };
      return;
    }
    const active = activeRequest.current;
    if (!active || active.requestId !== requestId || (!isSuccess && !isError)) return;
    // Clear before emitting: rerenders and StrictMode effect replay must not
    // report the same request twice. Cached results alone are not requests.
    activeRequest.current = undefined;
    const endedAt = isError ? Date.now() : (fulfilledTimeStamp ?? Date.now());
    tracking.requestCompleted(isError ? 'failure' : 'success', endedAt - active.startedAt);
  }, [
    coverageId,
    patientId,
    originalArgs,
    requestId,
    startedTimeStamp,
    fulfilledTimeStamp,
    isFetching,
    isSuccess,
    isError,
    tracking,
  ]);
  const status = query.error && 'status' in query.error ? query.error.status : undefined;
  // currentData is scoped to these query arguments. Never expose cached source
  // details while rechecking access, or after a denied/failed refresh.
  const data = query.error || query.isFetching ? undefined : query.currentData;
  const panel = (
    <PayerHistoryPanel
      data={data}
      coverageLabel={coverageLabel}
      onHistoryOpen={() => {
        tracking.historyOpened();
        void query.refetch();
      }}
      loading={query.isFetching || query.isLoading}
      error={!!query.error}
      denied={status === 401 || status === 403}
      onRefresh={() => {
        tracking.refreshClicked();
        void query.refetch();
      }}
      onClaimOpen={() => tracking.claimOpened()}
    />
  );
  return children ? children(panel) : panel;
}
